import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import OrderSearch from "../common/OrderSearch"; 

export default function SelectOrders({
  open,
  onClose,
  onDone, // Callback to return selected order numbers
  accountNumber,
  title = "Select Orders",
  maxSelection = null, // Optional limit on number of orders
}) {
  const [orderInput, setOrderInput] = useState("");
  const [isOrderValid, setIsOrderValid] = useState(false);
  const [orders, setOrders] = useState([]); // Array of order objects
  const [selectedOrders, setSelectedOrders] = useState([]); // Array of selected order numbers

  const handleOrderData = (orderData) => {
    if (orderData && !orders.some(order => order.orderNumber === orderData.orderNumber)) {
      // Add new order object to the list
      setOrders(prev => [...prev, orderData]);
      // Clear the search input
      setOrderInput("");
    }
  };

  const handleRemoveOrder = (orderNumber) => {
    setOrders(prev => prev.filter(order => order.orderNumber !== orderNumber));
    setSelectedOrders(prev => prev.filter(id => id !== orderNumber));
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedOrders(orders.map(order => order.orderNumber));
    } else {
      setSelectedOrders([]);
    }
  };

  const handleSelectOrder = (orderNumber) => {
    setSelectedOrders(prev => {
      if (prev.includes(orderNumber)) {
        return prev.filter(id => id !== orderNumber);
      } else {
        if (maxSelection && prev.length >= maxSelection) {
          return prev; // Don't add if max limit reached
        }
        return [...prev, orderNumber];
      }
    });
  };

  const handleDone = () => {
    onDone(selectedOrders);
    handleClose();
  };

  const handleClose = () => {
    // Reset state when closing
    setOrders([]);
    setSelectedOrders([]);
    setOrderInput("");
    onClose();
  };

  const isAllSelected = orders.length > 0 && selectedOrders.length === orders.length;
  const isIndeterminate = selectedOrders.length > 0 && selectedOrders.length < orders.length;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { height: "90vh" } }}
    >
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", gap: 1, pr: 6 }}
      >
        {title}
        {maxSelection && (
          <Chip size="small" label={`Max ${maxSelection} orders`} />
        )}
        <IconButton
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <OrderSearch
            value={orderInput}
            onChange={(e) => setOrderInput(e.target.value)}
            isValidated={setIsOrderValid}
            onOrderData={handleOrderData}
            accountNumber={accountNumber}
            placeholder="Search and add orders..."
          />
        </Box>

        {orders.length > 0 ? (
          <TableContainer component={Paper} variant="outlined" sx={{ flex: 1 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={isIndeterminate}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Order Number</TableCell>
                  <TableCell>Client Name</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {orders.map((order) => (
                  <TableRow key={order.orderNumber} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedOrders.includes(order.orderNumber)}
                        onChange={() => handleSelectOrder(order.orderNumber)}
                        disabled={
                          maxSelection &&
                          !selectedOrders.includes(order.orderNumber) &&
                          selectedOrders.length >= maxSelection
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <strong>#{order.orderNumber}</strong>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color="info" label={order.name} />
                    </TableCell>
                    <TableCell>{order.project}</TableCell>
                    <TableCell>
                      <Tooltip title="Remove order">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveOrder(order.orderNumber)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography color="text.secondary">
              No orders added yet. Search for orders above to get started.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          disableElevation
          onClick={handleDone}
          disabled={selectedOrders.length === 0}
        >
          Done ({selectedOrders.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
}
